import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PartnerReports() {
  const { token, user } = useAuth();
  const canDownload = user?.section_permissions?.reports_download !== false;
  const canSeeRevenue = user?.section_permissions?.statistics_revenue !== false;
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  
  // Month selector
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  const monthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const monthLabel = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    fetchPartners();
  }, [monthStr]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/partners-list?month=${monthStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (partnerName, format = 'csv') => {
    const downloadKey = `${partnerName}__${format}`;
    setDownloading(downloadKey);
    try {
      const response = await axios.get(
        `${API}/reports/partner/${encodeURIComponent(partnerName)}?month=${monthStr}&format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long' });
      link.download = `Report_${partnerName}_${monthName}_${selectedYear}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} report downloaded for ${partnerName}`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  const changeMonth = (delta) => {
    let m = selectedMonth + delta;
    let y = selectedYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setSelectedMonth(m);
    setSelectedYear(y);
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="partner-reports-title">Partner Reports</h1>
        <p className="text-slate-500 mt-1">Download monthly CSV or PDF reports per partner</p>
      </div>

      {/* Month Selector */}
      <div className="flex items-center justify-center gap-4 mb-8 bg-white rounded-xl p-4 shadow-sm border border-slate-200">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => changeMonth(-1)}
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <span className="text-lg font-semibold text-slate-800 min-w-[180px] text-center" data-testid="current-month-label">
          {monthLabel}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => changeMonth(1)}
          data-testid="next-month-btn"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Partners List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : partners.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No partner rides found for {monthLabel}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {partners.map((partner) => (
            <div
              key={partner.name}
              className="flex items-center justify-between bg-white rounded-xl p-5 shadow-sm border border-slate-200 hover:border-slate-300 transition-colors"
              data-testid={`partner-report-row-${partner.name}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{partner.name}</h3>
                  <p className="text-sm text-slate-500">
                    {partner.rides} ride{partner.rides !== 1 ? 's' : ''}{canSeeRevenue ? <> &middot; Total: &euro;{partner.total.toFixed(2)}</> : ''}
                  </p>
                </div>
              </div>
              {canDownload && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => downloadReport(partner.name, 'csv')}
                  disabled={downloading?.startsWith(partner.name + '__')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                  data-testid={`download-report-csv-${partner.name}`}
                >
                  {downloading === `${partner.name}__csv` ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  CSV
                </Button>
                <Button
                  onClick={() => downloadReport(partner.name, 'pdf')}
                  disabled={downloading?.startsWith(partner.name + '__')}
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-2"
                  data-testid={`download-report-pdf-${partner.name}`}
                >
                  {downloading === `${partner.name}__pdf` ? (
                    <span className="animate-spin h-4 w-4 border-2 border-rose-600 border-t-transparent rounded-full" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  PDF
                </Button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
